import { type ReactNode } from 'react';
import { cn } from '@/utils/styles';
import { Link } from '@design-system/Link';
import { InfoIcon } from '@/components/icons/InfoIcon';
import { EXPERIENCE_PICKER_PATH } from '@/constants/paths';
import { useLatestContext } from '@/hooks/context/useLatestContext';

export interface LatestResultsBannerProps {
  /** Advisory copy — passed by the host page; defaulted for the common case. */
  message?: ReactNode;
  /** Inline action label linking to the experience picker. */
  actionLabel?: string;
  /** Where the action navigates. Defaults to the experience picker. */
  to?: string;
  className?: string;
}

/**
 * DE-702 — floating advisory shown at the top of the formula / subscription /
 * shop / profile surfaces when the ACTIVE context is not the one holding the
 * latest test results. Self-gating: renders null unless the active context is
 * stale, so each mount is a plain drop-in with no per-page condition.
 */
export const LatestResultsBanner = ({
  message = 'Formulas based on latest test results.',
  actionLabel = 'Change context',
  // `reselect` forces the picker to show instead of auto-switching back into
  // the current (stale) context — see ExperiencePickerPage.
  to = `${EXPERIENCE_PICKER_PATH}?reselect=true`,
  className,
}: LatestResultsBannerProps) => {
  const { hasAnyResults, isActiveLatest } = useLatestContext();

  if (!hasAnyResults || isActiveLatest) return null;

  return (
    <div
      role="status"
      data-testid="latestResultsBanner"
      className={cn(
        // Elevated pill: hugs its content (not full-width), left-aligned in a
        // flex-column parent; white surface, hairline border, soft shadow.
        'inline-flex w-fit max-w-full items-center gap-3 self-start rounded-2xl border border-stroke-secondary-subtle bg-background px-4 py-3',
        'shadow-[0_1px_2px_rgba(16,24,40,0.04),0_4px_16px_-4px_rgba(16,24,40,0.10)]',
        className,
      )}
    >
      <InfoIcon aria-hidden className="size-[18px] shrink-0 text-secondary" />

      <p className="typography-body min-w-0 flex-1 text-primary">
        {message}
        <Link
          variant="underline"
          to={to}
          className="typography-body ml-2 font-bold text-primary underline-offset-4"
        >
          {actionLabel}
        </Link>
      </p>
    </div>
  );
};
